/**
 * ControlCanvas Component
 * Renders individual controls on the canvas with proper previews for all control types
 *
 * This component displays a design-time preview of controls with real component rendering
 * for QR codes, barcodes, rich text editors, and code editors to show users what they
 * will get at runtime.
 */

import React, { memo } from 'react';
import type { FormControl } from '@atlas-forms/types-js';
import '../../styles/canvas/ControlCanvas.css';
import {
  FileIcon, CodeIcon, EditIcon, CheckIcon, ChevronDownIcon,
  ClockIcon, BarChartIcon, HistogramIcon, WaterfallChartIcon,
  LineChartIcon, AreaChartIcon, ScatterPlotIcon, HeatmapIcon, GaugeIcon,
  PieChartIcon, PlayIcon, FormIcon,
} from '@atlas-forms/ui-components-react';
import {
  QRCodeControl, BarcodeControl, RichTextEditorControl, CodeEditorControl
} from '@atlas-forms/controls-advanced-react';

export interface ControlCanvasProps {
  control: FormControl;
  isSelected: boolean;
}

/**
 * Get icon for control type
 */
function getControlIcon(type: string): React.ReactNode {
  if (['text', 'textarea', 'email', 'url', 'password', 'rich-text-editor'].includes(type)) return <EditIcon width={16} height={16} />;
  if (['checkbox', 'radio', 'switch'].includes(type)) return <CheckIcon width={16} height={16} />;
  if (['select', 'multiselect'].includes(type)) return <ChevronDownIcon width={16} height={16} />;
  if (['date', 'datetime', 'time-picker', 'date-range-picker'].includes(type)) return <ClockIcon width={16} height={16} />;
  if (type === 'histogram') return <HistogramIcon width={16} height={16} />;
  if (type === 'waterfall-chart') return <WaterfallChartIcon width={16} height={16} />;
  if (type === 'line-chart') return <LineChartIcon width={16} height={16} />;
  if (type === 'area-chart') return <AreaChartIcon width={16} height={16} />;
  if (type === 'scatter-plot') return <ScatterPlotIcon width={16} height={16} />;
  if (['heatmap', 'tree-map'].includes(type)) return <HeatmapIcon width={16} height={16} />;
  if (['circular-gauge', 'linear-gauge', 'progress-ring'].includes(type)) return <GaugeIcon width={16} height={16} />;
  if (type === 'bar-chart') return <BarChartIcon width={16} height={16} />;
  if (['pie-chart', 'bubble-chart'].includes(type)) return <PieChartIcon width={16} height={16} />;
  if (type === 'button') return <PlayIcon width={16} height={16} />;
  if (['json-editor', 'enhanced-json-editor', 'code-editor', 'javascript-block', 'html', 'html-block', 'css', 'qr-code-generator', 'barcode-generator'].includes(type)) return <CodeIcon width={16} height={16} />;
  if (type.includes('chart')) return <BarChartIcon width={16} height={16} />;
  if (type.includes('graph')) return <ScatterPlotIcon width={16} height={16} />;
  return <FormIcon width={16} height={16} />;
}

/**
 * Get friendly name for control type
 */
function getControlLabel(type: string): string {
  return type
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Render control on canvas with memoization for performance
 */
const ControlCanvasComponent: React.FC<ControlCanvasProps> = ({ control, isSelected }) => {
  return (
    <div className={`af-control-canvas ${isSelected ? 'af-control-canvas--selected' : ''}`}>
      <div className="af-control-canvas__header">
        <span className="af-control-canvas__icon">{getControlIcon(control.type)}</span>
        <span className="af-control-canvas__type">{getControlLabel(control.type)}</span>
        {control.required && <span className="af-control-canvas__required">*</span>}
      </div>

      <div className="af-control-canvas__content">
        {control.label && (
          <div className="af-control-canvas__label">{control.label}</div>
        )}

        {/* Preview different control types */}
        {(control.type === 'text' || control.type === 'email' || control.type === 'number') && (
          <input
            type={control.type === 'email' ? 'email' : control.type === 'number' ? 'number' : 'text'}
            placeholder={control.placeholder || 'Enter value'}
            disabled
            className="af-control-canvas__input-preview"
          />
        )}

        {control.type === 'textarea' && (
          <textarea
            placeholder={control.placeholder || 'Enter value'}
            disabled
            className="af-control-canvas__textarea-preview"
          />
        )}

        {(control.type === 'checkbox' || control.type === 'switch') && (
          <input type="checkbox" disabled className="af-control-canvas__checkbox-preview" />
        )}

        {control.type === 'radio' && (
          <input type="radio" disabled className="af-control-canvas__radio-preview" />
        )}

        {(control.type === 'select' || control.type === 'multiselect') && (
          <select disabled className="af-control-canvas__select-preview">
            <option>{control.placeholder || 'Select...'}</option>
          </select>
        )}

        {control.type === 'date' && (
          <input type="date" disabled className="af-control-canvas__date-preview" />
        )}

        {control.type === 'button' && (
          <button disabled className="af-control-canvas__button-preview">
            {control.label || 'Button'}
          </button>
        )}

        {control.type === 'label' && (
          <span className="af-control-canvas__text-preview">{control.label}</span>
        )}

        {control.type === 'image' && (
          <div className="af-control-canvas__image-placeholder">
            Image Placeholder
          </div>
        )}

        {/* QR Code Generator - Real QR preview */}
        {control.type === 'qr-code-generator' && (
          <div style={{ padding: '12px', textAlign: 'center', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
            <QRCodeControl
              control={control}
              value={(control.config as any)?.value || 'https://example.com'}
              onChange={() => {}}
              onBlur={() => {}}
              mode="design"
            />
          </div>
        )}

        {/* Barcode Generator - Real barcode preview */}
        {control.type === 'barcode-generator' && (
          <div style={{ padding: '12px', textAlign: 'center', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
            <BarcodeControl
              control={control}
              value={(control.config as any)?.value || '123456789'}
              onChange={() => {}}
              onBlur={() => {}}
              mode="design"
            />
          </div>
        )}

        {/* Rich Text Editor - Preview with toolbar */}
        {control.type === 'rich-text-editor' && RichTextEditorControl && (
          <div style={{ padding: '8px', backgroundColor: '#f9f9f9', borderRadius: '4px', border: '1px solid #e0e0e0', minHeight: '60px' }}>
            <RichTextEditorControl
              control={control}
              value={`<p>${control.label || 'Rich Text Preview'}</p>`}
              onChange={() => {}}
              onBlur={() => {}}
              mode="design"
            />
          </div>
        )}

        {/* Rich Text Editor - Fallback if library not loaded */}
        {control.type === 'rich-text-editor' && !RichTextEditorControl && (
          <div style={{ padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '4px', border: '1px solid #e0e0e0', minHeight: '60px', color: '#666', fontSize: '12px' }}>
            Rich Text Editor Preview
          </div>
        )}

        {/* Code Editor - Preview with syntax highlighting */}
        {control.type === 'code-editor' && CodeEditorControl && (
          <div style={{ padding: '8px', backgroundColor: '#f9f9f9', borderRadius: '4px', border: '1px solid #e0e0e0', overflow: 'hidden', maxHeight: '150px' }}>
            <CodeEditorControl
              control={control}
              value={`// ${control.label || 'Code Editor Preview'}\nconsole.log('Hello, World!');`}
              onChange={() => {}}
              onBlur={() => {}}
              mode="design"
            />
          </div>
        )}

        {/* Code Editor - Fallback if library not loaded */}
        {control.type === 'code-editor' && !CodeEditorControl && (
          <div style={{ padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '4px', border: '1px solid #e0e0e0', color: '#666', fontSize: '12px' }}>
            Code Editor Preview
          </div>
        )}

        {(control.type.includes('chart') || control.type.includes('graph')) && (
          <div className="af-control-canvas__chart-preview">
            {/* Show chart preview with configured data */}
            <div style={{ padding: '10px', fontSize: '12px', color: '#666' }}>
              {(() => {
                const staticData = (control.config as any)?.staticData;
                const series = (control.config as any)?.series;

                if (!staticData || staticData.length === 0) {
                  return <span style={{ fontStyle: 'italic' }}>No data configured</span>;
                }

                return (
                  <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>
                      {staticData.length} items
                      {series?.length ? ` × ${series.length} series` : ''}
                    </div>
                    <div style={{ fontSize: '11px' }}>
                      {staticData.slice(0, 3).map((item: any, i: number) => (
                        <div key={i}>
                          {item.name}: {item.value}
                        </div>
                      ))}
                      {staticData.length > 3 && <div>+{staticData.length - 3} more</div>}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Simple bar chart preview SVG */}
            {(control.config as any)?.staticData?.length > 0 && (
              <svg style={{ width: '100%', height: '80px', marginTop: '8px' }} viewBox="0 0 200 80">
                {(() => {
                  const data = (control.config as any).staticData as Array<{name: string; value: number}>;
                  const maxValue = Math.max(...data.map(d => d.value), 1);
                  const barWidth = 200 / (data.length || 1) * 0.7;
                  const spacing = 200 / (data.length || 1);

                  return (
                    <>
                      {data.map((item, i) => {
                        const height = (item.value / maxValue) * 60;
                        const x = i * spacing + (spacing - barWidth) / 2;
                        const y = 70 - height;
                        return (
                          <rect
                            key={i}
                            x={x}
                            y={y}
                            width={barWidth}
                            height={height}
                            fill="#8884d8"
                            opacity="0.8"
                          />
                        );
                      })}
                    </>
                  );
                })()}
              </svg>
            )}
          </div>
        )}

        {control.helpText && (
          <small className="af-control-canvas__help-text">{control.helpText}</small>
        )}
      </div>

      <div className="af-control-canvas__meta">
        <small>{control.id}</small>
        {control.width && <small>{control.width}</small>}
      </div>
    </div>
  );
};

// Memoize for performance: only re-render when control or isSelected changes
export const ControlCanvas = memo(ControlCanvasComponent, (prev, next) => {
  return (
    prev.control.id === next.control.id &&
    prev.control.type === next.control.type &&
    prev.control.label === next.control.label &&
    prev.control.config === next.control.config &&
    prev.isSelected === next.isSelected
  );
});

ControlCanvas.displayName = 'ControlCanvas';
